<script>
import taskProgress from '@/utils/taskProgress';

import BaseCheckbox from '@/components/BaseCheckbox.vue';
import BaseTableHeaderItem from '@/components/base/BaseTableHeaderItem.vue';
import TaskStatus from '@/components/task/TaskStatus.vue';
import BasePriority from '@/components/BasePriority.vue';
import BaseDate from '@/components/BaseDate.vue';
import BaseProgressbar from '@/components/BaseProgressbar.vue';
import EmptyList from '@/components/ui/EmptyList.vue';
import LazyRenderer from '@/components/base/LazyRenderer.vue';
import UserUniversalTile from '@/components/UserUniversalTile.vue';
import AvatarEditable from '@/components/AvatarEditable.vue';
import FormBlock from '@/components/FormBlock.vue';
import BaseSelect from '@/components/BaseSelect.vue';
import BaseTooltipDropdown from '@/components/BaseTooltipDropdown.vue';

export default {
  name: 'TaskTable',
  components: {
    BaseTooltipDropdown,
    EmptyList,
    BaseCheckbox,
    BaseTableHeaderItem,
    TaskStatus,
    BasePriority,
    BaseDate,
    BaseProgressbar,
    LazyRenderer,
    UserUniversalTile,
    AvatarEditable,
    FormBlock,
    BaseSelect,
  },
  props: {
    tasks: Array,
    headers: Array,
    selectedTasks: Array,
  },
  data() {
    return {
      sort: null,
      order: null,
      sorterOptions: [
        { value: 'asc_priority', label: 'По приоритету', icon: 'arrow-up' },
        { value: 'desc_priority', label: 'По приоритету', icon: 'arrow-down' },
        { value: 'asc_dateFinish', label: 'По дате', icon: 'arrow-up' },
        { value: 'desc_dateFinish', label: 'По дате', icon: 'arrow-down' },
      ],
      sorter: {
        value: null,
        direction: null,
      },
    };
  },
  computed: {
    sortComputed() {
      return this.sort ?? (this.headers ?? []).find(i => !!i.sortFn && i.defaultSort);
    },

    sortValue() {
      if (this.sort === null && this.order === null) {
        return null;
      }

      const direction = this.order ? 'asc' : 'desc';
      return `${direction}_${this.sort.sortValue}`;
    },

    tasksSorted() {
      return !this.sortComputed
        ? this.tasks
        : [...this.tasks].sort((a, b) => this.sortComputed.sortFn(a, b) * (this.order ? 1 : -1));
    },

    taskProgress() {
      const tasks = {
        total: this.tasks.length,
        closed: this.tasks.filter((t) => t.isClosed).length,
      };
      return taskProgress(tasks.total, tasks.closed);
    },

    checkboxValue() {
      return (this.tasks.length === 0) ? false : this.selectedTasks.length === this.tasks.length;
    },
  },
  methods: {
    sortBy(header) {
      if (this.sort === header) {
        this.order = !this.order;
      } else {
        this.sort = header;
        this.order = header.defaultOrder ?? this.order ?? true;
      }
    },

    changeSorter(value) {
      if (!value) this.sorter = { value, direction: null };
      else {
        const splitter = value.split('_');
        this.sorter = { value: splitter[1], direction: splitter[0] };
      }
    },

    getCheckBoxValue(task) {
      return this.selectedTasks.find((t) => t.id === task.id) !== undefined;
    },

    getHeader(value) {
      if (value) {
        const splitter = value.split('_');
        const sortHeader = this.headers.find(header => header.header.sortValue === splitter[1]);
        this.sort = sortHeader?.header;
        if (splitter[0] === 'asc') {
          this.order = true;
        }

        if (splitter[0] === 'desc') {
          this.order = false;
        }
      } else {
        this.sort = null;
        this.order = null;
      }
    },

    updateTask(field, taskId, value) {
      const id = taskId;
      this.$emit('updateTask', { id, field, value });
    },

    makeUserList(task) {
      const projectMembers = task.project.projectMembers.map(member => member.user);
      projectMembers.push(task.project.author);
      return projectMembers;
    },
  },
};
</script>

<template lang="pug">
FormBlock.b-table-wrapper(mobile)
  .b-headers
    BaseCheckbox._checkbox(:value="checkboxValue" :disabled="!tasks.length" @change="$emit('toggleTasks', tasks)")
    BaseTableHeaderItem._item(v-for="header in headers" :key="header.header.title" :class="{ [header.header.className]: true }" v-bind="header" @sort="sortBy")
    BaseSelect._sorter(:value="sortValue" :options="sorterOptions" @input="getHeader" notSelectedLabel="По умолчанию" label="Сортировать по")
  .b-table
    template(v-if="tasks.length")
      LazyRenderer.b-row(v-for="task in tasksSorted" :key="task.id" :class="{ closed: task.isClosed }")
        BaseCheckbox._checkbox(:value="getCheckBoxValue(task)" @change="$emit('toggleTask', task)")
        router-link._name(v-if="task.project && task.stage" :to="{ name: 'task-detail', params: { projectId: task.project.id, stageId: task.stage.id, taskId: task.id } }" target='_blank') {{ task.name }}
        router-link._project(:to="{ name: 'project-stages', params: { projectId: task.project.id } }" target='_blank')
          ._project-avatar
            AvatarEditable._project-avatar-container(:avatar="task.project.avatar" :gray="task.isClosed" placeholder="no-avatar-project" :editable="false")
            BaseTooltipDropdown._project-tooltip(:text="task.project.name" dark no-wrap)
        ._status
          TaskStatus(
            :value="task.status"
            :gray="task.isClosed"
            :changeable="task.canBeUpdated"
            :editable="task.project.canEditProject"
            :project="task.project"
            @change="updateTask('status', task.id, $event)"
            @edited="$emit('statusesUpdated', { id: task.project.id, items: $event })"
          )
        ._priority
          BasePriority(:level="task.priority" :gray="task.isClosed" :editable="task.canBeUpdated" @change="updateTask('priority', task.id, $event)")
        ._date._date-cell
          BaseDate(:date="task.dateFinish" :gray="task.isClosed" :editable="task.canBeUpdated" @change="updateTask('dateFinish', task.id, $event)")
        ._author
          UserUniversalTile(:selected="task.author" label="Нет автора" :items="makeUserList(task)" :gray="task.isClosed" :isClearable="false" :editable="task.canBeUpdated" @change="updateTask('author', task.id, $event)")
        ._arrow
          Icon(name="angle-down")
        ._manager
          UserUniversalTile(:selected="task.manager" label="Нет исполнителя" :items="makeUserList(task)" :gray="task.isClosed" :editable="task.canBeUpdated" @change="updateTask('manager', task.id, $event)")
    template(v-else)
      EmptyList(label="Нет задач")
  .progress-bar
    BaseProgressbar(v-bind="taskProgress")
</template>

<style scoped lang="scss">
.table-wrapper {
  &__progress-bar {
    width: 360px;
    max-width: 100%;
  }
}

.table {
  display: flex;
  flex-direction: column;
  gap: 1px;
  padding-top: 25px;
  padding-bottom: 57px;
}

.row {
  width: 100%;
  display: grid;
  //grid-template-columns: 20px calc(15% - 30px) 6% 10% 8% 8% 17% 17%;
  grid-template-columns: 20px 13fr 6fr 15fr 8fr 8fr 17fr 17fr;
  align-items: center;
  box-sizing: border-box;
  padding: 15px 22px;
  border-radius: 6px;
  gap: 20px;
  background-color: rgba(248, 249, 252, 1);

  &__name {
    //margin-left: -20px;
    display: inline-flex;
    align-items: center;
    justify-content: flex-start;
    color: var(--dark-gray, #1E3147);
    font-family: Geometria, sans-serif;
    font-size: 13px;
    font-style: normal;
    font-weight: 500;
    line-height: 19px; /* 146.154% */
  }

  &__status, &__priority {
    justify-self: start;
    max-width: 100%;
    //overflow: hidden;
  }

  &__project-avatar {
    position: relative;
  }

  &__project-tooltip {
    display: none;
  }

  &__project-avatar:hover &__project-tooltip {
    visibility: visible;
    display: block;
    opacity: 1;
    top: 40px;
    left: 70px;
  }

  &__project-avatar-container {
    width: 40px;
    height: 40px;
    &::v-deep svg { cursor: pointer !important; }
    border-width: 2px;
    border-radius: 16px;
  }

  &__arrow {
    display: none;
    color: rgba(153, 163, 197, 1);
    rotate: -90deg;
    justify-self: center;
  }

  &__projetct-tooltip {
    display: block;
    visibility: visible;
    opacity: 1;
  }
}

.headers {
  display: grid;
  //grid-template-columns: 30px calc(15% - 10px) 6% 10% 8% 8% 17% 17%;
  grid-template-columns: 20px 13fr 6fr 15fr 8fr 8fr 17fr 17fr;
  align-items: center;
  box-sizing: border-box;
  padding: 0 22px;
  gap: 20px;

  &__item {
    //margin-left: -30px;
  }

  &__sorter {
    display: none;
  }
}

.closed {
  background: #e6ecf2;
  color: #c5c5c5;
}

@media (max-width: 1399px) {
  .headers {
    padding: 0;
    grid-template-columns: 20px 1fr 20%;

    &__item {
      display: none;
    }

    &__sorter {
      display: block;
      grid-column: 3;

      &::v-deep .select__inner {
        font-size: 11px;
      }
    }
  }

  .row {
    background-color: transparent;
    padding: 20px 0;
    border-bottom: 1px rgba(209, 217, 240, 1) solid;
    gap: 20px;
    grid-template-columns: 20px auto min-content auto 1fr  min-content min-content;
    grid-auto-rows: auto;
    grid-template-areas:
    "chk name name name name date date"
    ". auth arw mng sts prt prj";

    &__checkbox { grid-area: chk; }
    &__name { grid-area: name; }
    &__date { grid-area: date; }
    &__author { grid-area: auth; }
    &__manager { grid-area: mng; }
    &__arrow { grid-area: arw; }
    &__status { grid-area: sts; }
    &__priority { grid-area: prt; }
    &__project { grid-area: prj; }

    &__arrow {
      display: flex;
    }

    &__date-cell {
      &::v-deep .datetime {
        text-align: right;
      }
    }

    &__status {
      justify-self: end;
    }

    &__name {
      margin-left: 0;
    }
  }
}

@media (max-width: 1000px) {
  .row {
    &__author::v-deep .avatar,
    &__manager::v-deep .avatar,
    &__project-avatar {
      width: 30px;
      height: 30px;
      border-radius: 12px;
      border-width: 1px;
    }

    &__author, &__manager {
      &::v-deep .name {
        width: min-content;
      }
    }
  }

  .headers {
    grid-template-columns: 20px 1fr max-content;
  }
}

@media (max-width: 768px) {
  .row {
    column-gap: 10px;
    row-gap: 14px;

    &__author, &__manager {
      &::v-deep {
        .name, .body {
          display: none;
        }
      }
    }
  }
}

@media (max-width: 500px) {
  .row {
    &__author::v-deep .avatar,
    &__manager::v-deep .avatar,
    &__project-avatar {
      width: 20px;
      height: 20px;
      border-radius: 7px;
    }
  }
}
</style>
